/* Same-product packs; checkout discounts are administered by Shopify. */
if (!customElements.get('native-bundle')) {
  customElements.define('native-bundle', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        this.product = JSON.parse(this.querySelector('[data-bundle-product]').textContent);
        this.offers = [...this.querySelectorAll('[data-offer]')];
        if (!this.offers.length || !this.product.variants.length) return;
        this.form = this.querySelector('[data-bundle-form]');
        this.button = this.querySelector('[data-bundle-submit]');
        this.buttonLabel = this.querySelector('[data-button-label]');
        this.buyNowButton = this.querySelector('[data-bundle-buy-now]');
        this.idleLabel = this.buttonLabel.textContent;
        this.errorElement = this.querySelector('[data-bundle-error]');
        this.successElement = this.querySelector('[data-bundle-success]');
        this.formatter = new Intl.NumberFormat(this.dataset.locale || 'en', {
          style: 'currency', currency: this.dataset.currency || 'USD'
        });
        this.defaultVariant = this.product.variants.find(v => String(v.id) === this.dataset.defaultVariant)
          || this.product.variants.find(v => v.available) || this.product.variants[0];
        this.querySelectorAll('[data-item] [data-variant]').forEach(select => select.value = String(this.defaultVariant.id));
        if (!this.activeOffer()) {
          const initial = this.offers.find(offer => offer.dataset.tier === this.dataset.defaultTier) || this.offers[0];
          initial.querySelector('input[name=pack]').checked = true;
        }
        this.onChange = event => {
          this.showError('');
          this.successElement.hidden = true;
          this.refresh();
          if (event.target.matches('[data-variant], input[name=pack]')) this.updatePrimaryVariant();
        };
        this.onClick = event => {
          const add = event.target.closest('[data-add-item]');
          const remove = event.target.closest('[data-remove-item]');
          if (add && this.contains(add)) this.addItem(add.closest('[data-offer]'));
          if (remove && this.contains(remove)) this.removeItem(remove.closest('[data-offer]'), remove.closest('[data-item]'));
        };
        this.onSubmit = event => this.submit(event);
        this.form.addEventListener('change', this.onChange);
        this.form.addEventListener('submit', this.onSubmit);
        this.form.addEventListener('click', this.onClick);
        this.refresh();
        this.querySelector('[data-bundle-enhanced]').hidden = false;
        this.querySelector('[data-bundle-fallback]').hidden = true;
      } catch (error) {
        // Keep the ordinary Shopify product form usable if enhancement cannot start.
        console.error('Bundle selector could not initialize.', error);
      }
    }

    disconnectedCallback() {
      this.form?.removeEventListener('change', this.onChange);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.form?.removeEventListener('click', this.onClick);
      this.initialized = false;
    }

    money(cents) { return this.formatter.format(cents / 100); }

    variantFor(row) {
      const variantSelect = row.querySelector?.('[data-variant]');
      if (variantSelect) return this.product.variants.find(variant => String(variant.id) === variantSelect.value);
      const selects = [...row.querySelectorAll('[data-option]')];
      if (!selects.length) return this.defaultVariant;
      return this.product.variants.find(v => selects.every(select =>
        v.options[Number(select.dataset.option)] === select.value));
    }

    activeOffer() { return this.offers.find(offer => offer.querySelector('input[name=pack]').checked); }

    totals(offer) {
      const variants = [...offer.querySelectorAll('[data-item]')].map(row => this.variantFor(row));
      // Display estimates only after the merchant explicitly enables matching rules.
      const rate = this.dataset.discountsReady === 'true' ? Number(offer.dataset.discount) : 0;
      const subtotal = variants.reduce((sum, variant) => sum + (variant?.price || 0), 0);
      const total = Math.round(subtotal * (100 - rate) / 100);
      const compare = rate > 0 ? subtotal : variants.reduce((sum, variant) => sum + Math.max(variant?.compare_at_price || 0, variant?.price || 0), 0);
      const unitPrice = Math.round(subtotal / variants.length * (100 - rate) / 100);
      const unitCompare = Math.round(compare / variants.length);
      const mixedPrices = new Set(variants.map(variant => variant?.price)).size > 1;
      return { variants, subtotal, total, compare, rate, unitPrice, unitCompare, mixedPrices };
    }

    addItem(offer) {
      if (this.busy || offer?.dataset.expandable !== 'true') return;
      const rows = [...offer.querySelectorAll('[data-item]')];
      if (rows.length >= Number(offer.dataset.maxQuantity)) return;
      const template = offer.querySelector('[data-item-template]');
      const newRow = template.content.firstElementChild.cloneNode(true);
      const select = newRow.querySelector('[data-variant]');
      select.value = rows.at(-1)?.querySelector('[data-variant]')?.value || String(this.defaultVariant.id);
      offer.querySelector('[data-item-list]').append(newRow);
      this.numberItems(offer);
      this.showError('');
      this.successElement.hidden = true;
      this.refresh();
      select.focus();
    }

    removeItem(offer, row) {
      if (this.busy || offer?.dataset.expandable !== 'true') return;
      const rows = [...offer.querySelectorAll('[data-item]')];
      if (rows.length <= Number(offer.dataset.quantity) || rows.indexOf(row) < Number(offer.dataset.quantity)) return;
      row.remove();
      this.numberItems(offer);
      this.showError('');
      this.successElement.hidden = true;
      this.refresh();
      const add = offer.querySelector('[data-add-item]');
      if (!add.closest('[data-add-row]').hidden) add.focus();
    }

    numberItems(offer) {
      const rows = [...offer.querySelectorAll('[data-item]')];
      rows.forEach((row, index) => {
        const number = index + 1;
        const select = row.querySelector('[data-variant]');
        const id = `${this.id}-tier-${offer.dataset.tier}-item-${number}`;
        select.id = id;
        select.setAttribute('aria-label', `Options for item ${number}`);
        const label = row.querySelector('[data-item-number]');
        label.htmlFor = id;
        label.textContent = `#${number}`;
        const remove = row.querySelector('[data-remove-item]');
        remove.hidden = number <= Number(offer.dataset.quantity);
        remove.setAttribute('aria-label', `Remove item ${number}`);
      });
    }

    itemsFor(offer) {
      const grouped = new Map();
      for (const row of offer.querySelectorAll('[data-item]')) {
        const variant = this.variantFor(row);
        if (!variant?.available) throw new Error('Choose an available size and color for every item.');
        const existing = grouped.get(variant.id);
        if (existing) existing.quantity += 1;
        else grouped.set(variant.id, { id: variant.id, quantity: 1 });
      }
      const items = [...grouped.values()];
      for (const item of items) {
        const variant = this.product.variants.find(v => v.id === item.id);
        const rule = variant.quantity_rule || { min: 1, increment: 1 };
        if (item.quantity < rule.min || (rule.max != null && item.quantity > rule.max) || item.quantity % rule.increment !== 0) {
          throw new Error('This pack does not meet the quantity requirements for one of your chosen variants. Choose another pack or options.');
        }
      }
      return items;
    }

    refresh() {
      const active = this.activeOffer();
      for (const offer of this.offers) {
        const selected = offer === active;
        offer.classList.toggle('is-selected', selected);
        const panel = offer.querySelector('.nb-items');
        panel.hidden = !selected;
        panel.querySelectorAll('select').forEach(select => select.disabled = !selected || this.busy);
        const totals = this.totals(offer);
        const valid = totals.variants.every(Boolean);
        offer.querySelector('[data-total]').textContent = valid ? this.money(totals.total) : 'Unavailable';
        const compare = offer.querySelector('[data-compare]');
        compare.textContent = this.money(totals.compare);
        compare.hidden = !valid || totals.compare <= totals.total;
        const savings = offer.querySelector('[data-savings]');
        savings.hidden = !valid || !totals.rate;
        savings.textContent = `SAVE ${totals.rate}%`;
        offer.querySelector('[data-unit-price]').textContent = valid ? this.money(totals.unitPrice) : '';
        const unitCompare = offer.querySelector('[data-unit-compare]');
        unitCompare.hidden = !valid || totals.unitCompare <= totals.unitPrice;
        unitCompare.textContent = this.money(totals.unitCompare);
        offer.querySelector('[data-average-note]').hidden = !valid || !totals.mixedPrices;
        offer.querySelectorAll('[data-item]').forEach(row => {
          const variant = this.variantFor(row);
          row.querySelector('[data-item-status]').textContent = !variant ? 'This combination is unavailable.' : !variant.available ? 'Sold out — choose another option.' : '';
        });
        if (offer.dataset.expandable === 'true') {
          const count = totals.variants.length;
          offer.querySelector('[data-next-item-number]').textContent = `#${count + 1}`;
          offer.querySelector('[data-add-row]').hidden = count >= Number(offer.dataset.maxQuantity);
          offer.querySelector('[data-add-item]').disabled = Boolean(this.busy);
        }
      }
      let valid = Boolean(active);
      try { if (active) this.itemsFor(active); } catch (error) { valid = false; }
      this.button.disabled = !valid || Boolean(this.busy);
      if (this.buyNowButton) this.buyNowButton.disabled = !valid || Boolean(this.busy);
      const totals = active ? this.totals(active) : null;
      this.querySelector('[data-discount-note]').hidden = !totals?.rate;
      this.buttonLabel.textContent = valid ? this.idleLabel : 'Choose available options';
    }

    updatePrimaryVariant() {
      const row = this.activeOffer()?.querySelector('[data-item]');
      const variant = row && this.variantFor(row);
      if (!variant) return;
      const info = this.closest('product-info');
      if (!info) return;
      if (variant.featured_media?.id) {
        info.querySelector('media-gallery')?.setActiveMedia?.(`${this.dataset.section}-${variant.featured_media.id}`, true);
      }
      const price = info.querySelector(`[id="price-${this.dataset.section}"]`);
      if (price) {
        const sale = variant.compare_at_price > variant.price;
        price.querySelector('.price')?.classList.toggle('price--on-sale', sale);
        price.querySelector('.price')?.classList.toggle('price--sold-out', !variant.available);
        const regular = price.querySelector('.price__regular .price-item--regular');
        const comparison = price.querySelector('.price__sale .price-item--regular');
        const salePrice = price.querySelector('.price-item--sale');
        if (regular) regular.textContent = this.money(variant.price);
        if (comparison) comparison.textContent = this.money(variant.compare_at_price || variant.price);
        if (salePrice) salePrice.textContent = this.money(variant.price);
      }
      if (info.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    showError(message) {
      this.errorElement.textContent = message;
      this.errorElement.hidden = !message;
    }

    showSuccess(quantity) {
      this.successElement.replaceChildren(document.createTextNode(`${quantity} ${quantity === 1 ? 'item' : 'items'} added. `));
      const link = document.createElement('a');
      link.href = this.dataset.cartUrl;
      link.textContent = 'View cart';
      this.successElement.append(link);
      this.successElement.hidden = false;
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy) return;
      const checkout = event.submitter?.dataset.intent === 'checkout';
      this.showError('');
      this.successElement.hidden = true;
      let items;
      try { items = this.itemsFor(this.activeOffer()); }
      catch (error) { this.showError(error.message); return; }
      this.busy = true;
      this.form.querySelector('.nb-offers').disabled = true;
      this.button.disabled = true;
      if (this.buyNowButton) this.buyNowButton.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.buttonLabel.textContent = 'Adding…';
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sections = cart?.getSectionsToRender?.().map(section => section.id) || [];
      cart?.setActiveElement?.(this.button);
      let added = false;
      try {
        const body = { items };
        if (sections.length) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(typeof data.description === 'string' ? data.description : 'These items could not be added. Check availability and your cart before trying again.');
        }
        added = true;
        const quantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.showSuccess(quantity);
        if (checkout) {
          window.location.assign(this.dataset.checkoutUrl);
          return;
        }
        if (!cart || sections.some(id => !data.sections?.[id])) {
          window.location.assign(this.dataset.cartUrl);
          return;
        }
        // Dawn's notification needs a single line key even for a multi-item add.
        const first = data.items?.[0] || data;
        try {
          cart.classList.remove('is-empty');
          cart.renderContents({ ...data, id: first.id, key: first.key });
        } catch (error) {
          // Do not turn a successful add into a retry that could duplicate items.
          window.location.assign(this.dataset.cartUrl);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'native-bundle', productVariantId: first.variant_id || first.id, cartData: data });
        }
      } catch (error) {
        if (!added) {
          this.showError(error instanceof TypeError
            ? 'The connection was interrupted. Check your cart before trying again.' : error.message);
        }
      } finally {
        this.busy = false;
        this.form.querySelector('.nb-offers').disabled = false;
        this.button.removeAttribute('aria-busy');
        this.refresh();
      }
    }
  });
}
